import sections from "@/content/strategicComplexitySections.json";

export const strategicComplexityArticle = {
  slug: "strategic-complexity-burden",
  path: "/strategy/strategic-complexity-burden",
  title: "When Complexity Stops Creating Optionality",
  shortTitle: "When Complexity Stops Creating Optionality",
  subtitle: "The Strategic Complexity Burden",
  deck: "A diagnostic framework for identifying when product, market, customer and organisational variety outgrows the coordination capacity required to support it.",
  category: "Strategy Framework",
  author: "Fu Research",
  authorType: "Organization",
  authorPath: "/about",
  publishedAt: "2026-09-13",
  modifiedAt: "2026-09-13",
  seoTitle: "Strategic Complexity Burden | Fu Research",
  description: "Fu Research introduces the Strategic Complexity Burden: a diagnostic framework for determining when strategic variety and interdependence exceed an organisation’s capacity to coordinate them.",
  summary: "A diagnostic framework for identifying when product, market, customer and organisational variety outgrows the coordination capacity required to support it.",
  tags: ["Strategy", "Organisational Design", "Complexity", "Competitive Advantage", "Operating Model"],
  layoutVariant: "strategy-note",
  archivePath: "/strategy",
  archiveLabel: "Strategy & Frameworks",
  endLabel: "End of strategy note",
  contents: { waitForCollapse: true },
  prelude: {
    title: "Opening thesis",
    blocks: [{ text: "Complexity is not the problem. Unsupported complexity is. Strategic variety creates opportunity only while the organisation can absorb the coordination it demands." }],
  },
  atAGlance: {
    thesisLabel: "Core insight",
    thesis: "Strategic variety becomes a burden when coordination demand grows faster than organisational capacity.",
    problemLabel: "Coordination Demand",
    problem: "Driven by both coordination workload and the interdependence between activities.",
    problemDetail: {
      label: "Organisational Capacity",
      text: "People, processes, data and clear decision rights determine the ability to process decisions.",
    },
    frameworkLabel: "Framework",
    frameworkLead: "Strategic Complexity Burden =",
    frameworkDimensions: ["Coordination Demand", "Organisational Capacity"],
    frameworkOperator: "/",
    frameworkDetail: {
      label: "Key warning signal",
      text: "Growth produces disproportionate delays, exceptions, rework, escalation and coordination effort — signals of an approaching capacity frontier.",
    },
    decisionLabel: "Strategic implication",
    decisionLead: "Retain complexity that creates competitive value. Remove complexity the organisation cannot economically support; do not minimise complexity indiscriminately.",
    decisionsLabel: "Management responses",
    decisions: [
      "Scale — Low demand / High capacity",
      "Orchestrate — High demand / High capacity",
      "Stabilise — Low demand / Low capacity",
      "De-scope — High demand / Low capacity",
    ],
    coreQuestionLabel: "Core question",
    coreQuestion: "When does strategic variety stop creating optionality and begin creating organisational burden?",
  },
  hero: {
    kind: "framework",
    coverKind: "photograph",
    coverSrc: "/images/strategy/strategic-complexity-burden-city-cover.webp",
    editorialSrc: "/images/strategy/strategic-complexity-burden-city-hero.webp",
    editorialMobileSrc: "/images/strategy/strategic-complexity-burden-city-mobile.webp",
    coverAlt: "Pedestrians and street traffic beneath glass office towers in Canary Wharf, London, rendered in restrained steel-grey tones",
    coverCredit: "Unsplash",
    coverSource: "https://images.unsplash.com/photo-1762792049326-d86838f80c0d",
    coverCaption: "Street-level activity in Canary Wharf, London.",
    socialSrc: "/images/strategy/strategic-complexity-burden-city-og.jpg",
    alt: "When Complexity Stops Creating Optionality — a contemporary financial-district street scene in Canary Wharf, London",
    cardFit: "cover",
  },
  relatedArticleIds: [
    "when-growth-weakens-competitive-advantages",
    "how-lvmh-centralizes-scale-without-standardizing-luxury",
    "how-apple-built-an-ecosystem-that-customers-rarely-leave",
  ],
  sections,
};